<template>
  <div
    class="demo-controls"
    :class="{ 'is-final-step': isFinalStep }"
    role="toolbar"
    aria-label="Demo navigation controls"
  >
    <!-- Back Button -->
    <button
      v-if="showBackButton"
      class="demo-controls__button demo-controls__button--secondary"
      :disabled="!canGoBack"
      :title="backButtonTitle"
      aria-label="Go to previous step"
      @click="handleBack"
    >
      <svg
        class="demo-controls__icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 19l-7-7 7-7"
        />
      </svg>
      {{ backButtonLabel }}
    </button>

    <!-- Spacer for layout -->
    <div class="demo-controls__spacer" />

    <!-- Close/Skip Button -->
    <button
      v-if="showCloseButton && !isFinalStep"
      class="demo-controls__button demo-controls__button--danger"
      :title="closeButtonTitle"
      aria-label="Close demo"
      @click="handleClose"
    >
      <svg
        class="demo-controls__icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
      {{ closeButtonLabel }}
    </button>

    <!-- Next Button (Always Enabled) -->
    <button
      v-if="!isFinalStep"
      class="demo-controls__button demo-controls__button--primary"
      :title="nextButtonTitle"
      aria-label="Go to next step"
      @click="handleNext"
    >
      {{ nextButtonLabel }}
      <svg
        class="demo-controls__icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 5l7 7-7 7"
        />
      </svg>
    </button>

    <!-- Finish Button (on final step) -->
    <button
      v-else
      class="demo-controls__button demo-controls__button--primary demo-controls__button--finish"
      :title="finishButtonTitle"
      aria-label="Complete demo"
      @click="handleFinish"
    >
      <svg
        class="demo-controls__icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M5 13l4 4L19 7"
        />
      </svg>
      {{ finishButtonLabel }}
    </button>
  </div>
</template>

<script>
import {
  DEMO_CONTROLS_CONFIG,
  BUTTON_STATES,
  DEMO_CONTROLS_ACCESSIBILITY,
} from '@/demo/constants';

/**
 * DemoControls Component
 * Provides navigation buttons for demo flow with synchronization service integration
 * - Back button: Navigate to previous step
 * - Next button: Always enabled; simulates interaction on click if needed
 * - Close button: Exit demo early
 * - Finish button: Complete demo on final step
 * 
 * Supports keyboard navigation:
 * - Page Up / Ctrl+Up: Previous step
 * - Page Down / Ctrl+Down: Next step
 * - Escape: Close demo
 *
 * @component
 * @example
 * <DemoControls
 *   :current-step="2"
 *   :total-steps="13"
 *   @next="advanceStep"
 *   @back="previousStep"
 *   @close="closeDemo"
 *   @finish="finishDemo"
 * />
 */
export default {
  name: 'DemoControls',

  props: {
    /**
     * Current step index (0-based)
     */
    currentStep: {
      type: Number,
      default: 0,
      validator: (value) => value >= 0,
    },

    /**
     * Total number of steps
     */
    totalSteps: {
      type: Number,
      default: 13,
      validator: (value) => value > 0,
    },

    /**
     * Show back button
     */
    showBackButton: {
      type: Boolean,
      default: true,
    },

    /**
     * Show close button
     */
    showCloseButton: {
      type: Boolean,
      default: true,
    },

    /**
     * Disable keyboard shortcuts
     */
    disableKeyboardShortcuts: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether the current step's target element is ready.
     * Next button is disabled until true.
     */
    nextButtonReady: {
      type: Boolean,
      default: true,
    },
  },

  emits: {
    /**
     * Emitted when back button is clicked
     */
    back: null,

    /**
     * Emitted when next button is clicked
     */
    next: null,

    /**
     * Emitted when close button is clicked
     */
    close: null,

    /**
     * Emitted when finish button is clicked
     */
    finish: null,
  },

  data() {
    return {
      DEMO_CONTROLS_CONFIG,
      BUTTON_STATES,
      DEMO_CONTROLS_ACCESSIBILITY,
    };
  },

  computed: {
    /**
     * Check if this is the final step
     */
    isFinalStep() {
      return this.currentStep === this.totalSteps - 1;
    },

    /**
     * Can navigate to previous step
     */
    canGoBack() {
      return this.currentStep > 0;
    },

    /**
     * Can navigate to next step (always true for Next button)
     */
    canGoNext() {
      return !this.isFinalStep;
    },

    /**
     * Proxy for the nextButtonReady prop — used in template and handleNext.
     */
    targetReady() {
      return this.nextButtonReady;
    },

    /**
     * Back button label
     */
    backButtonLabel() {
      return DEMO_CONTROLS_CONFIG.BACK_BUTTON.label;
    },

    /**
     * Back button title
     */
    backButtonTitle() {
      return this.canGoBack
        ? DEMO_CONTROLS_CONFIG.BACK_BUTTON.title
        : 'This is the first step';
    },

    /**
     * Next button label
     */
    nextButtonLabel() {
      return DEMO_CONTROLS_CONFIG.NEXT_BUTTON.label;
    },

    /**
     * Next button title
     */
    nextButtonTitle() {
      return DEMO_CONTROLS_CONFIG.NEXT_BUTTON.title;
    },

    /**
     * Close button label
     */
    closeButtonLabel() {
      return DEMO_CONTROLS_CONFIG.CLOSE_BUTTON.label;
    },

    /**
     * Close button title
     */
    closeButtonTitle() {
      return DEMO_CONTROLS_CONFIG.CLOSE_BUTTON.title;
    },

    /**
     * Finish button label
     */
    finishButtonLabel() {
      return DEMO_CONTROLS_CONFIG.FINISH_BUTTON.label;
    },

    /**
     * Finish button title
     */
    finishButtonTitle() {
      return DEMO_CONTROLS_CONFIG.FINISH_BUTTON.title;
    },
  },

  mounted() {
    // Setup keyboard shortcuts
    if (!this.disableKeyboardShortcuts && DEMO_CONTROLS_ACCESSIBILITY.ENABLE_KEYBOARD_SHORTCUTS) {
      window.addEventListener('keydown', this.handleKeydown);
    }
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown);
  },

  methods: {
    /**
     * Handle back button click
     */
    handleBack() {
      if (this.canGoBack) {
        this.$emit('back');
        this.announceNavigation('Previous step');
      }
    },

    /**
     * Handle next button click
     * Always enabled; simulates interaction if needed
     */
    handleNext() {
      if (!this.canGoNext) {
        return;
      }

      console.log('[DemoControls] Next button clicked at step', this.currentStep);

      // Emit next event - do NOT emit simulated interaction
      // The manual next button handler will advance the step, 
      // and emitting interaction would cause double advancement via the global listener
      this.$emit('next');
      this.announceNavigation('Next step');
    },

    /**
     * Handle close button click
     */
    handleClose() {
      this.$emit('close');
      this.announceNavigation('Demo closed');
    },

    /**
     * Handle finish button click
     */
    handleFinish() {
      this.$emit('finish');
      this.announceNavigation('Demo completed');
    },

    /**
     * Handle keyboard shortcuts
     * PageUp: Previous step
     * PageDown: Next step
     * Escape: Close demo
     */
    handleKeydown(event) {
      // Don't interfere with input fields
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }

      const key = event.key;
      const isCtrlOrCmd = event.ctrlKey || event.metaKey;

      // Previous step
      if (key === 'PageUp' || (key === 'ArrowUp' && isCtrlOrCmd)) {
        event.preventDefault();
        this.handleBack();
      }

      // Next step
      if (key === 'PageDown' || (key === 'ArrowDown' && isCtrlOrCmd)) {
        event.preventDefault();
        this.handleNext();
      }

      // Close demo
      if (key === 'Escape' && this.showCloseButton) {
        event.preventDefault();
        this.handleClose();
      }

      // Space to advance (on body element only)
      if (key === ' ' && event.target === document.body) {
        event.preventDefault();
        if (this.canGoNext) {
          this.handleNext();
        }
      }
    },

    /**
     * Announce navigation to screen readers
     */
    announceNavigation(message) {
      if (DEMO_CONTROLS_ACCESSIBILITY.ANNOUNCE_NAVIGATION) {
        try {
          // Create announcement element
          const announcement = document.createElement('div');
          announcement.setAttribute('role', 'status');
          announcement.setAttribute('aria-live', 'polite');
          announcement.setAttribute('aria-atomic', 'true');
          announcement.className = 'sr-only';
          announcement.textContent = message;
          document.body.appendChild(announcement);

          // Remove after announcement
          setTimeout(() => {
            document.body.removeChild(announcement);
          }, 1000);
        } catch (error) {
          console.warn('[DemoControls] Failed to announce:', error);
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.demo-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #1e1e2e;
  border-top: 1px solid #2a2a3e;
  animation: demo-controls-enter 300ms ease-out;

  @media (max-width: 480px) {
    flex-wrap: wrap;
    padding: 12px;
    gap: 8px;

    .demo-controls__button {
      flex: 1;
      min-width: calc(50% - 4px);
    }
  }
}

@keyframes demo-controls-enter {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.demo-controls__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 5px 12px;
  height: 30px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms ease-out;
  white-space: nowrap;
  user-select: none;

  &:focus-visible {
    outline: 2px solid #007aff;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  @media (max-width: 480px) {
    height: 28px;
    font-size: 12px;
    padding: 4px 10px;
  }
}

.demo-controls__button--primary {
  background-color: #007aff;
  color: #ffffff;

  &:hover:not(:disabled) {
    background-color: #0051d5;
    box-shadow: 0 2px 8px rgba(0, 122, 255, 0.3);
  }

  &.demo-controls__button--finish {
    background-color: #22c55e;

    &:hover:not(:disabled) {
      background-color: #16a34a;
      box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
    }
  }
}

.demo-controls__button--secondary {
  background-color: #2a2a3e;
  color: #e0e0e0;

  &:hover:not(:disabled) {
    background-color: #3a3a52;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
}

.demo-controls__button--danger {
  background-color: #ff3b30;
  color: #ffffff;

  &:hover:not(:disabled) {
    background-color: #e81828;
    box-shadow: 0 2px 8px rgba(255, 59, 48, 0.3);
  }
}

.demo-controls__icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.demo-controls__spacer {
  flex: 1;
}

.is-final-step {
  .demo-controls__button--danger {
    display: none;
  }
}

// Accessibility: Screen reader only content
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
